import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, Target, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Public verified track record banner. Shows a 3-segment breakdown
// (T2 full-target / T1 partial / SL loss) so users can analyse the
// quality of wins, not just the headline win-rate.
export default function AccuracyBanner({ segment }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/accuracy/global${segment ? `?segment=${segment}` : ""}`);
      setStats(data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [segment]); // eslint-disable-line

  const total = stats?.total_closed ?? 0;
  const winRate = stats?.win_rate ?? 0;
  const t1Only = stats?.t1_only_rate ?? 0;
  const t2Rate = stats?.full_target_rate ?? 0;
  const lossRate = stats?.loss_rate ?? 0;
  const open = stats?.open ?? 0;
  const minProb = stats?.min_probability ?? 65;
  const hasSample = total >= 10;

  return (
    <section
      className="border-b border-zinc-900 px-4 md:px-6 py-2.5 bg-gradient-to-r from-[#0e0e0e] via-[#0a0a0a] to-[#0e0e0e]"
      data-testid="accuracy-banner"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-5 flex-wrap">
          <div className="flex items-center gap-2">
            <Target className="h-3.5 w-3.5 text-[#22C55E]" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">Verified track record</span>
            {segment && (
              <span className={`text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 border ${segment === "indian" ? "border-[#F59E0B]/40 text-[#F59E0B]" : "border-[#3B9BFF]/40 text-[#3B9BFF]"}`} data-testid="accuracy-segment-tag">
                {segment === "indian" ? "Indian markets" : "Global markets"}
              </span>
            )}
            <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 border border-[#007AFF]/40 text-[#007AFF]" data-testid="accuracy-threshold-tag" title={`Stats and live signals are filtered to trades with ${minProb}% or higher probability.`}>
              ≥ {minProb}% only
            </span>
            <span
              className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 border border-zinc-700 text-zinc-500 cursor-help"
              data-testid="accuracy-methodology-tag"
              title="One open trade per symbol+mode. Trend flips invalidate the prior trade (excluded from stats). 30m/4h cooldown after a close prevents whipsaw entries. Watchlist scanned every 15 min to keep recording independent of user activity."
            >
              1-trade-rule
            </span>
          </div>

          {hasSample ? (
            <>
              <div className="flex items-baseline gap-1.5" data-testid="accuracy-win-rate">
                <span className={`font-heading font-black text-2xl tracking-tight ${winRate >= 65 ? "text-[#22C55E]" : winRate >= 50 ? "text-[#F59E0B]" : "text-[#EF4444]"}`}>{winRate}%</span>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">win rate</span>
              </div>

              {/* T1 / T2 / SL breakdown — single source of truth for analysing trade quality */}
              <div className="flex items-center gap-3 text-[11px] font-mono" data-testid="accuracy-breakdown">
                <div className="flex items-center gap-1.5" title={`Hit Target 2 (full target): ${stats.hit_t2} trades`}>
                  <span className="h-2 w-2 bg-[#22C55E] inline-block" />
                  <span className="text-zinc-300 font-bold" data-testid="accuracy-t2-rate">T2 {t2Rate}%</span>
                  <span className="text-zinc-500 hidden md:inline">({stats.hit_t2})</span>
                </div>
                <div className="flex items-center gap-1.5" title={`Hit Target 1 only (partial win): ${stats.hit_t1} trades`}>
                  <span className="h-2 w-2 bg-[#16A34A]/60 inline-block" />
                  <span className="text-zinc-300 font-bold" data-testid="accuracy-t1-rate">T1 {t1Only}%</span>
                  <span className="text-zinc-500 hidden md:inline">({stats.hit_t1})</span>
                </div>
                <div className="flex items-center gap-1.5" title={`Hit Stop-Loss: ${stats.losses} trades`}>
                  <span className="h-2 w-2 bg-[#EF4444] inline-block" />
                  <span className="text-zinc-300 font-bold" data-testid="accuracy-loss-rate">SL {lossRate}%</span>
                  <span className="text-zinc-500 hidden md:inline">({stats.losses})</span>
                </div>
              </div>

              <div className="text-[11px] font-mono text-zinc-500 hidden lg:block" data-testid="accuracy-sample">
                <span className="text-zinc-200 font-bold">{total}</span> trades closed · <span className="text-[#007AFF]">{open}</span> live
              </div>
            </>
          ) : (
            <div className="text-[11px] font-mono text-zinc-400" data-testid="accuracy-building">
              <span className="text-[#007AFF] font-bold">◢ Building track record</span> · <span className="text-zinc-300">{total}</span> closed · <span className="text-zinc-300">{open}</span> live · sample &lt; 10 → win-rate hidden
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
          {hasSample && (
            <span className="hidden md:inline-flex items-center gap-1.5" title="A win = trade reached at least Target 1">
              <TrendingUp className="h-3 w-3 text-[#22C55E]" />
              Win = T1 or T2
            </span>
          )}
          {isAdmin && (
            <button onClick={load} disabled={loading} className="text-zinc-500 hover:text-white" data-testid="accuracy-refresh" title="Refresh">
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            </button>
          )}
        </div>
      </div>

      {/* Visual 3-segment bar showing the proportion of T2 / T1 / SL */}
      {hasSample && (
        <div className="mt-2 h-1 w-full bg-zinc-900 flex overflow-hidden" data-testid="accuracy-bar">
          <div className="h-full bg-[#22C55E] transition-all" style={{ width: `${t2Rate}%` }} title={`T2 ${t2Rate}%`} />
          <div className="h-full bg-[#16A34A]/60 transition-all" style={{ width: `${t1Only}%` }} title={`T1 only ${t1Only}%`} />
          <div className="h-full bg-[#EF4444] transition-all" style={{ width: `${lossRate}%` }} title={`SL ${lossRate}%`} />
        </div>
      )}
    </section>
  );
}
